"use client"

import { useState, useCallback } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Terminal, Copy, Check, Wand2, AlertCircle } from "lucide-react"
import { toast } from "sonner"

type OutputLanguage =
  | "javascript-fetch"
  | "javascript-axios"
  | "javascript-xhr"
  | "python-requests"
  | "python-urllib"
  | "php-curl"
  | "go"
  | "rust"
  | "csharp"
  | "java"

interface ParsedCurl {
  method: string
  url: string
  headers: Record<string, string>
  data?: string
  dataType?: "json" | "form" | "raw"
}

function parseCurl(curlCommand: string): ParsedCurl {
  // Clean up the command
  let cmd = curlCommand
    .replace(/\\\n/g, " ")
    .replace(/\s+/g, " ")
    .trim()

  // Remove 'curl' prefix
  if (cmd.toLowerCase().startsWith("curl ")) {
    cmd = cmd.slice(5)
  }

  const result: ParsedCurl = {
    method: "GET",
    url: "",
    headers: {},
  }

  // Parse URL (can be with or without quotes, can have -X before it)
  const urlMatch = cmd.match(/(?:^|\s)['"]?(https?:\/\/[^\s'"]+)['"]?/i) ||
                   cmd.match(/(?:^|\s)['"]?([^\s'"]+\.[^\s'"]+[^\s'"]*)['"]?/)
  if (urlMatch) {
    result.url = urlMatch[1]
  }

  // Parse method -X or --request
  const methodMatch = cmd.match(/-X\s*['"]?(\w+)['"]?/i) ||
                      cmd.match(/--request\s*['"]?(\w+)['"]?/i)
  if (methodMatch) {
    result.method = methodMatch[1].toUpperCase()
  }

  // Parse headers -H or --header
  const headerRegex = /-H\s*['"]([^'"]+)['"]/gi
  let headerMatch
  while ((headerMatch = headerRegex.exec(cmd)) !== null) {
    const [key, ...valueParts] = headerMatch[1].split(":")
    if (key && valueParts.length > 0) {
      result.headers[key.trim()] = valueParts.join(":").trim()
    }
  }

  // Parse data -d or --data or --data-raw
  const dataMatch = cmd.match(/(?:-d|--data|--data-raw)\s*['"]([^'"]+)['"]/i) ||
                    cmd.match(/(?:-d|--data|--data-raw)\s*'([^']+)'/i) ||
                    cmd.match(/(?:-d|--data|--data-raw)\s*"([^"]+)"/i)
  if (dataMatch) {
    result.data = dataMatch[1]
    result.method = result.method === "GET" ? "POST" : result.method

    // Detect data type
    try {
      JSON.parse(dataMatch[1])
      result.dataType = "json"
    } catch {
      if (dataMatch[1].includes("=")) {
        result.dataType = "form"
      } else {
        result.dataType = "raw"
      }
    }
  }

  return result
}

function generateCode(parsed: ParsedCurl, language: OutputLanguage): string {
  const { method, url, headers, data } = parsed

  switch (language) {
    case "javascript-fetch":
      return generateFetch(method, url, headers, data)
    case "javascript-axios":
      return generateAxios(method, url, headers, data)
    case "javascript-xhr":
      return generateXhr(method, url, headers, data)
    case "python-requests":
      return generatePythonRequests(method, url, headers, data)
    case "python-urllib":
      return generatePythonUrllib(method, url, headers, data)
    case "php-curl":
      return generatePhpCurl(method, url, headers, data)
    case "go":
      return generateGo(method, url, headers, data)
    case "rust":
      return generateRust(method, url, headers, data)
    case "csharp":
      return generateCSharp(method, url, headers, data)
    case "java":
      return generateJava(method, url, headers, data)
    default:
      return ""
  }
}

function generateFetch(method: string, url: string, headers: Record<string, string>, data?: string): string {
  const options: string[] = []

  if (method !== "GET") {
    options.push(`  method: '${method}'`)
  }

  if (Object.keys(headers).length > 0) {
    const headerStr = Object.entries(headers)
      .map(([k, v]) => `    '${k}': '${v}'`)
      .join(",\n")
    options.push(`  headers: {\n${headerStr}\n  }`)
  }

  if (data) {
    options.push(`  body: ${JSON.stringify(data)}`)
  }

  if (options.length === 0) {
    return `fetch('${url}')
  .then(response => response.json())
  .then(data => console.log(data))
  .catch(error => console.error('Error:', error));`
  }

  return `fetch('${url}', {
${options.join(",\n")}
})
  .then(response => response.json())
  .then(data => console.log(data))
  .catch(error => console.error('Error:', error));`
}

function generateAxios(method: string, url: string, headers: Record<string, string>, data?: string): string {
  const config: string[] = [`  url: '${url}'`]

  if (method !== "GET") {
    config.push(`  method: '${method.toLowerCase()}'`)
  }

  if (Object.keys(headers).length > 0) {
    const headerStr = Object.entries(headers)
      .map(([k, v]) => `    '${k}': '${v}'`)
      .join(",\n")
    config.push(`  headers: {\n${headerStr}\n  }`)
  }

  if (data) {
    config.push(`  data: ${data}`)
  }

  return `axios({
${config.join(",\n")}
})
  .then(response => console.log(response.data))
  .catch(error => console.error(error));`
}

function generateXhr(method: string, url: string, headers: Record<string, string>, data?: string): string {
  const headerLines = Object.entries(headers)
    .map(([k, v]) => `xhr.setRequestHeader('${k}', '${v}');`)
    .join("\n")

  return `const xhr = new XMLHttpRequest();
xhr.open('${method}', '${url}');
${headerLines}
xhr.onload = function() {
  if (xhr.status === 200) {
    console.log(JSON.parse(xhr.responseText));
  }
};
xhr.onerror = function() {
  console.error('Request failed');
};
xhr.send(${data ? JSON.stringify(data) : ""});`
}

function generatePythonRequests(method: string, url: string, headers: Record<string, string>, data?: string): string {
  const lines = ["import requests", ""]

  if (Object.keys(headers).length > 0) {
    const headerStr = Object.entries(headers)
      .map(([k, v]) => `    '${k}': '${v}'`)
      .join(",\n")
    lines.push(`headers = {\n${headerStr}\n}`)
  }

  const args = [`'${url}'`]
  if (Object.keys(headers).length > 0) args.push("headers=headers")
  if (data) args.push(`data='${data}'`)

  lines.push("")
  lines.push(`response = requests.${method.toLowerCase()}(${args.join(", ")})`)
  lines.push("print(response.json())")

  return lines.join("\n")
}

function generatePythonUrllib(method: string, url: string, headers: Record<string, string>, data?: string): string {
  return `import urllib.request
import json

${data ? `data = '${data}'.encode('utf-8')` : "data = None"}
req = urllib.request.Request('${url}', data=data, method='${method}')
${Object.entries(headers).map(([k, v]) => `req.add_header('${k}', '${v}')`).join("\n")}

with urllib.request.urlopen(req) as response:
    result = json.loads(response.read().decode('utf-8'))
    print(result)`
}

function generatePhpCurl(method: string, url: string, headers: Record<string, string>, data?: string): string {
  const headerArray = Object.entries(headers)
    .map(([k, v]) => `    '${k}: ${v}'`)
    .join(",\n")

  return `<?php
$ch = curl_init();

curl_setopt_array($ch, [
    CURLOPT_URL => '${url}',
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_CUSTOMREQUEST => '${method}',${headerArray ? `
    CURLOPT_HTTPHEADER => [
${headerArray}
    ],` : ""}${data ? `
    CURLOPT_POSTFIELDS => '${data}',` : ""}
]);

$response = curl_exec($ch);
curl_close($ch);

echo $response;
?>`
}

function generateGo(method: string, url: string, headers: Record<string, string>, data?: string): string {
  const headerLines = Object.entries(headers)
    .map(([k, v]) => `\treq.Header.Set("${k}", "${v}")`)
    .join("\n")

  return `package main

import (
\t"fmt"
\t"io"
\t"net/http"${data ? `
\t"strings"` : ""}
)

func main() {
\t${data ? `body := strings.NewReader(\`${data}\`)
\treq, err := http.NewRequest("${method}", "${url}", body)` : `req, err := http.NewRequest("${method}", "${url}", nil)`}
\tif err != nil {
\t\tpanic(err)
\t}
${headerLines}

\tclient := &http.Client{}
\tresp, err := client.Do(req)
\tif err != nil {
\t\tpanic(err)
\t}
\tdefer resp.Body.Close()

\trespBody, _ := io.ReadAll(resp.Body)
\tfmt.Println(string(respBody))
}`
}

function generateRust(method: string, url: string, headers: Record<string, string>, data?: string): string {
  const headerLines = Object.entries(headers)
    .map(([k, v]) => `        .header("${k}", "${v}")`)
    .join("\n")

  return `use reqwest;

#[tokio::main]
async fn main() -> Result<(), Box<dyn std::error::Error>> {
    let client = reqwest::Client::new();
    let response = client
        .${method.toLowerCase()}("${url}")
${headerLines}${data ? `
        .body("${data}")` : ""}
        .send()
        .await?
        .text()
        .await?;

    println!("{}", response);
    Ok(())
}`
}

function generateCSharp(method: string, url: string, headers: Record<string, string>, data?: string): string {
  const headerLines = Object.entries(headers)
    .map(([k, v]) => `    client.DefaultRequestHeaders.Add("${k}", "${v}");`)
    .join("\n")

  return `using System;
using System.Net.Http;
using System.Threading.Tasks;

class Program
{
    static async Task Main()
    {
        using var client = new HttpClient();
${headerLines}

        ${data
          ? `var content = new StringContent("${data}");
        var response = await client.${method === "GET" ? "GetAsync" : method === "POST" ? "PostAsync" : `SendAsync(new HttpRequestMessage(HttpMethod.${method}, `}"${url}"${method !== "GET" && method !== "POST" ? ")" : ", content"});`
          : `var response = await client.${method === "GET" ? "GetAsync" : "SendAsync(new HttpRequestMessage(HttpMethod." + method + ", "}("${url}")${method !== "GET" ? ")" : ""};`}
        var result = await response.Content.ReadAsStringAsync();
        Console.WriteLine(result);
    }
}`
}

function generateJava(method: string, url: string, headers: Record<string, string>, data?: string): string {
  const headerLines = Object.entries(headers)
    .map(([k, v]) => `            .header("${k}", "${v}")`)
    .join("\n")

  return `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;

public class Main {
    public static void main(String[] args) throws Exception {
        HttpClient client = HttpClient.newHttpClient();
        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create("${url}"))
            .method("${method}", ${data ? `HttpRequest.BodyPublishers.ofString("${data}")` : "HttpRequest.BodyPublishers.noBody()"})
${headerLines}
            .build();

        HttpResponse<String> response = client.send(request,
            HttpResponse.BodyHandlers.ofString());
        System.out.println(response.body());
    }
}`
}

const languageOptions = [
  { value: "javascript-fetch", label: "JavaScript (fetch)" },
  { value: "javascript-axios", label: "JavaScript (axios)" },
  { value: "javascript-xhr", label: "JavaScript (XMLHttpRequest)" },
  { value: "python-requests", label: "Python (requests)" },
  { value: "python-urllib", label: "Python (urllib)" },
  { value: "php-curl", label: "PHP (cURL)" },
  { value: "go", label: "Go" },
  { value: "rust", label: "Rust (reqwest)" },
  { value: "csharp", label: "C# (HttpClient)" },
  { value: "java", label: "Java (HttpClient)" },
]

export function CurlConverter() {
  const [curlInput, setCurlInput] = useState("")
  const [output, setOutput] = useState("")
  const [language, setLanguage] = useState<OutputLanguage>("javascript-fetch")
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const convert = useCallback(() => {
    if (!curlInput.trim()) {
      toast.error("Please enter a cURL command")
      return
    }

    setError(null)

    try {
      const parsed = parseCurl(curlInput)

      if (!parsed.url) {
        throw new Error("Could not parse URL from cURL command")
      }

      const code = generateCode(parsed, language)
      setOutput(code)
      toast.success("Converted successfully")
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to parse cURL"
      setError(message)
      toast.error(message)
    }
  }, [curlInput, language])

  const copy = useCallback(() => {
    if (!output) return
    navigator.clipboard.writeText(output)
    setCopied(true)
    toast.success("Copied to clipboard")
    setTimeout(() => setCopied(false), 2000)
  }, [output])

  const loadSample = useCallback(() => {
    setCurlInput(`curl -X POST 'https://api.example.com/users' \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer token123' \\
  -d '{"name": "John", "email": "john@example.com"}'`)
    setError(null)
    setOutput("")
  }, [])

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Terminal className="h-5 w-5" />
            cURL to Code Converter
          </CardTitle>
          <CardDescription>
            Convert cURL commands to JavaScript, Python, PHP, Go, Rust, C#, and Java code.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1 space-y-2">
              <Label>Output Language</Label>
              <Select value={language} onValueChange={(v) => setLanguage(v as OutputLanguage)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {languageOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={loadSample}>
                Load Sample
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Input */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">cURL Command</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder="curl -X GET 'https://api.example.com/data'"
              value={curlInput}
              onChange={(e) => {
                setCurlInput(e.target.value)
                setError(null)
              }}
              className="min-h-[250px] font-mono text-sm"
            />
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}
            <Button onClick={convert} className="w-full">
              <Wand2 className="h-4 w-4 mr-2" />
              Convert
            </Button>
          </CardContent>
        </Card>

        {/* Output */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Generated Code</CardTitle>
              {output && (
                <Button variant="outline" size="sm" onClick={copy}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  <span className="ml-2">{copied ? "Copied" : "Copy"}</span>
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={output}
              readOnly
              placeholder="Converted code will appear here..."
              className="min-h-[250px] font-mono text-sm"
            />
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
